/* unplugin-vue-components disabled */
import {h, render} from 'vue'
import {EditableEditor, filterEditableOptions} from './editable-defs'
import Chip from './chip.vue'
import Suggestions from './suggestions.vue'

export default {
  mixins: [ModelValue(String)],
  props: {
    options: {type: Array, default: () => []},
    placeholder: {type: String, default: ''},
    editorDisabled: {type: Boolean, default: false},
    rows: {type: [String, Number], default: 1},
    maxRows: {type: [String, Number], default: undefined},
    maxlength: {type: Number, default: undefined},
    minlength: {type: Number, default: undefined},
    required: {type: Boolean, default: false},
    spellcheck: {type: Boolean, default: true},
    lineHeight: {type: [String, Number], default: 1},
    chipHeight: {type: [String, Number], default: undefined},
    allowResize: {type: Boolean, default: true},
  },
  emits: ['blur', 'change', 'focus', 'focusin', 'focusout', 'keydown', 'keyup', 'paste', 'rows', 'update:valid'],
  data() {
    return {
      internal_rows: Number(this.rows) || 1,
      valid: true,
      editor: undefined,
      suggestion_target: undefined
    }
  },
  computed: {
    editable() {
      return this.editorDisabled ? 'false' : 'true'
    },
    max_rows() {
      return Number(this.maxRows) || Number(this.rows) || 1
    },
    style_vars() {
      return {
        '--line-height': this.c_line_height,
        '--chip-height': this.c_chip_height,
        '--min-height': this.getHeight(this.internal_rows),
        '--max-height': this.getHeight(this.max_rows)
      }
    },
    c_line_height() {
      return this.toCssSize(this.lineHeight)
    },
    c_chip_height() {
      return this.toCssSize(this.chipHeight || this.lineHeight)
    },

  },
  watch: {
    options(options) {
      if (!this.editor) return
      this.editor.options = options

      if (document.activeElement === this.$refs.textarea) {
        this.renderText(this.editor.text, this.editor.caret)
      }
    },
    value: {
      handler() {
        this.reset()
      },
      immediate: true
    },
    valid() {
      this.$emit('update:valid', this.valid)
    }
  },
  mounted() {
    this.editor = new EditableEditor(this.$refs.textarea, this.options)
  },
  methods: {
    getContentWidth() {
      const input = this.$refs.textarea
      if (!input || !this.value) return 0

      // Measure the rendered text and chips, excluding the editor's padding.
      const range = document.createRange()
      range.selectNodeContents(input)
      return range.getBoundingClientRect().width
    },
    reset(){
      this.syncInputValue()
      this.checkValidity()
      this.startUpdateRows()
    },
    focus(ev) {
      if (ev && this.$refs.textarea.contains(ev?.target)) {
        return
      }
      this.moveCaretToEnd()
    },
    blur() {
      this.$refs.textarea.blur()
    },
    async onResize() {
      // update row when suggestions are open, because they need to be repositioned
      if (this.hasSuggestions()) {
        this.startUpdateRows()
      }
    },
    getHeight(rows) {
      if (typeof this.lineHeight === 'number') return `${rows * this.lineHeight}px`

      const match = String(this.lineHeight).match(/^(\d*\.?\d+)(.*)$/)
      if (!match) return this.lineHeight

      return `${Number(match[1]) * rows}${match[2]}`
    },
    toCssSize(value) {
      return typeof value === 'number' ? `${value}px` : value
    },
    checkValidity() {
      const length = this.value?.length || 0
      this.valid = (!this.required || length > 0)
        && (this.minlength === undefined || length >= this.minlength)
        && (this.maxlength === undefined || length <= this.maxlength)
      return this.valid
    },
    onInput() {
      const caret = this.editor.caret
      let text = this.editor.text

      if (this.maxlength !== undefined && text.length > this.maxlength) {
        text = text.slice(0, this.maxlength)
        this.renderText(text)
        this.value = this.normalizeValue(text)
        return
      }

      if (this.suggestion_target && this.hasSuggestions() && !this.isCommand(this.editor.active_token.text)) {
        this.renderSuggestions()
      }
      else {
        this.renderText(text, caret)
      }
      
      this.value = this.normalizeValue(text)
    },
    onKeydown(ev) {
      if (/^[0-9]$/.test(ev.key)) {
        ev.stopPropagation()
      }

      if (this.hasSuggestions()) {
        const key = ev.key
        const navigation_keys = ['ArrowDown', 'ArrowUp', 'Enter', 'Tab']

        if (navigation_keys.includes(key)) {
          ev.preventDefault()
          return
        }
        const token = this.editor.active_token
        const is_empty_token = token.text.length === 1
        const is_token_at_end = token.end === this.editor.text.length
        const token_end = token.end

        if (key === 'ArrowRight') {
          if (is_empty_token && !is_token_at_end) {
            this.unmountSuggestions()
            return
          }

          if (is_token_at_end) {
            ev.preventDefault()
            return
          }

          this.editor.setCaret(token_end + 1)
          return
        }

        if (key === 'ArrowLeft') {
          this.unmountSuggestions()
          return
        }

        if (key === ' ') {
          ev.preventDefault()
          this.editor.insertText(' ', this.maxlength)
          return
        }
      }

      if (ev.key === 'Backspace' && this.removeChipBeforeCaret()) {
        ev.preventDefault()
        return
      }

      this.$emit('keydown', ev)
    },

    onKeyup(ev){
      if (['ArrowLeft', 'ArrowRight'].includes(ev.key)) {
        if (this.suggestion_target) {
          this.renderSuggestions()
        }
        else if (this.hasSuggestions()) {
          this.renderText(this.editor.text, this.editor.caret)
        }
      }
      this.$emit('keyup', ev)
    },
    onPaste(ev) {
      ev.preventDefault()
      const text = ev.clipboardData.getData('text/plain')
      this.editor.insertText(text, this.maxlength)
      this.$emit('paste', ev)
    },
    hasSuggestions(token = this.editor.active_token) {
      return token && filterEditableOptions(token, this.options).length > 0
    },
    isCommand(command) {
      return this.options.some(item => item.command === command)
    },
    syncInputValue() {
      if (!this.editor || this.editor.text === (this.value || '')) return
      this.renderText(this.value || '')
    },
    renderText(text, caret = text.length) {
      this.unmountSuggestions()
      this.editor.render(text, caret)
      this.renderChips()
      this.renderSuggestions()
    },
    renderChips() {
      this.$refs.textarea.querySelectorAll('.chip-wrapper').forEach(el => {
        const vnode = h(Chip, {
          command: el.dataset.value, 
          options: this.options,
          onDelete: () => this.deleteChip(el)
        })
        vnode.appContext = this.$.appContext
        render(vnode, el)
      })
    },
    async renderSuggestions() {
      const target = this.$refs.textarea?.querySelector('.suggestions-mount')
      const token = this.editor.active_token

      if (!target || !this.hasSuggestions(token)) {
        this.unmountSuggestions()
        return
      }

      if (this.suggestion_target && this.suggestion_target !== target) {
        this.unmountSuggestions()
      }
      this.suggestion_target = target

      const vnode = h(Suggestions, {
        options: this.options,
        activeToken: token,
        onKeepFocus: this.keepFocus,
        onSelect: this.selectOption
      })

      vnode.appContext = this.$.appContext
      render(vnode, target)
      await this.$nextTick()
      this.startUpdateRows()
    },

    deleteChip(chip_node){
      render(null, chip_node)
      const {text, caret} = this.editor.removeChipNode(chip_node)
      this.renderText(text, caret)
      this.value = this.normalizeValue(text)
    },
    unmountSuggestions() {
      if (!this.suggestion_target) return
      render(null, this.suggestion_target)
      this.suggestion_target = undefined
    },
    normalizeValue(text) {
      return text.trim() === '' ? '' : text
    },
    async startUpdateRows() {
      this.internal_rows = Number(this.rows) || 1
      await this.$nextTick()
      this.updateRows()
    },
    updateRows() {
      const input = this.$refs.textarea
      if (!input) return

      const line_height = parseFloat(getComputedStyle(input).lineHeight)
      if (!line_height) return

      const min_rows = Number(this.rows) || 1
      const scroll_height = Math.max(input.scrollHeight, line_height)

      let rows = Math.ceil(scroll_height / line_height)

      if (rows > 1 && !this.allowResize) {
        this.internal_rows = min_rows
        this.$emit('rows', this.internal_rows + 1)
        return
      }
      this.internal_rows = Math.min(Math.max(rows, min_rows), this.max_rows)
      this.$emit('rows', this.internal_rows)
    },
    keepFocus() {
      if (this.editorDisabled) return
      this.$refs.textarea?.focus({preventScroll: true})
    },
    moveCaretToEnd() {
      this.editor.moveCaretToEnd()
    },
    selectOption(item) {
      const token = this.editor.active_token
      const text = this.editor.text
      const command = item.command + ' '
      const next = token
        ? text.slice(0, token.start) + command + text.slice(token.end)
        : text + command
      const caret = (token?.start || text.length) + command.length
      this.renderText(next, caret)
      this.value = this.normalizeValue(next)
    },
    removeChipBeforeCaret() {
      const next = this.editor.removeChipBeforeCaret()
      if (!next) return false

      this.renderText(next.text, next.caret)
      this.value = this.normalizeValue(next.text)
      return true
    },
    async setCommand(command){
      this.value = this.value === '' ? `${command} ` : `${this.value} ${command} `
      this.renderText(this.value)
    }
  }
}
